import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { shallow } from '@jwp/ott-common/src/utils/compare';
import { useMutation } from 'react-query';
import type { CustomFormField } from '@jwp/ott-common/types/account';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import { isTruthy, logDev } from '@jwp/ott-common/src/utils/common';
import { formatConsents, formatConsentsFromValues, formatConsentsToRegisterFields, formatConsentValues } from '@jwp/ott-common/src/utils/collection';
import useToggle from '@jwp/ott-hooks-react/src/useToggle';
import Visibility from '@jwp/ott-theme/assets/icons/visibility.svg?react';
import VisibilityOff from '@jwp/ott-theme/assets/icons/visibility_off.svg?react';
import env from '@jwp/ott-common/src/env';
import { useProfileStore } from '@jwp/ott-common/src/stores/ProfileStore';

import type { FormSectionContentArgs, FormSectionProps } from '../Form/FormSection';
import Alert from '../Alert/Alert';
import Button from '../Button/Button';
import Form from '../Form/Form';
import IconButton from '../IconButton/IconButton';
import TextField from '../TextField/TextField';
import FormFeedback from '../FormFeedback/FormFeedback';
import EditAccountPassword from '../../containers/AccountModal/forms/EditAccountPassword';
import Icon from '../Icon/Icon';
import { modalURLFromLocation } from '../../utils/location';
import { useAriaAnnouncer } from '../../containers/AnnouncementProvider/AnnoucementProvider';

import styles from './Account.module.scss';
import { createUserProfile, getUserProfile, editProfile } from '@jwp/ott-common/src/services/integrations/CustomAccountService';
import { getUserProfileData } from '@jwp/ott-common/src/helpers/AuthHelpers';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

type Props = {
  panelClassName?: string;
  panelHeaderClassName?: string;
  canUpdateEmail?: boolean;
};

interface FormErrors {
  email?: string;
  confirmationPassword?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  form?: string;
}

const Account = ({ panelClassName, panelHeaderClassName, canUpdateEmail = true }: Props): JSX.Element => {
  const accountController = getModule(AccountController);
  const {
    isLastnameRequired,
    isSplitupFullname,
    isCleeng,
    isCustomApiEnabled,
    canDeleteAccount,
    canShowExportData,
    canChangePassword,
    isInplayer,
    isKatonCognitoEnabled
  } = useAppConfig();

  const { t, i18n } = useTranslation(['user', 'account']);

  const announce = useAriaAnnouncer();
  const navigate = useNavigate();
  const location = useLocation();
  const [viewPassword, toggleViewPassword] = useToggle();
  const exportData = useMutation(accountController.exportAccountData);
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [showPasswordContent, setShowPasswordContent] = useState(false);
  const exportDataMessage = exportData.isSuccess ? t('account.export_data_success') : t('account.export_data_error');
  const htmlLang = i18n.language !== env.APP_DEFAULT_LANGUAGE ? env.APP_DEFAULT_LANGUAGE : undefined;

  useEffect(() => {
    if (exportData.isSuccess || exportData.isError) {
      setIsAlertVisible(true);
    }
  }, [exportData.isSuccess, exportData.isError]);

  const { customer, customerConsents, publisherConsents, subscription, allSubscriptions } = useAccountStore(
    ({ user, customerConsents, publisherConsents, subscription, allSubscriptions }) => ({
      customer: user,
      customerConsents,
      publisherConsents,
      subscription,
      allSubscriptions,
    }),
    shallow,
  );

  const { canChangePasswordWithOldPassword } = accountController.getFeatures();
  // users authenticated with social (register_source: facebook, google, twitter) do not have password by default
  const registerSource = customer?.metadata?.register_source;
  const isSocialLogin = (registerSource && registerSource !== 'inplayer') || false;
  const shouldAddPassword = (isSocialLogin && !customer?.metadata?.has_password) || false;
  const { cognitoUser } = useAccountStore.getState();
  // load consents (move to `useConsents` hook?)
  useEffect(() => {
    if (!publisherConsents) {
      accountController.getPublisherConsents();
    }
  }, [accountController, publisherConsents]);

  const [termsConsents, nonTermsConsents] = useMemo(() => {
    const terms: CustomFormField[] = [];
    const nonTerms: CustomFormField[] = [];

    publisherConsents?.forEach((consent) => {
      if (!consent?.type || consent?.type === 'checkbox') {
        terms.push(consent);
      } else {
        nonTerms.push(consent);
      }
    });

    return [terms, nonTerms];
  }, [publisherConsents]);

  const consents = useMemo(() => formatConsents(publisherConsents, customerConsents), [publisherConsents, customerConsents]);

  const consentsValues = useMemo(() => formatConsentValues(publisherConsents, customerConsents), [publisherConsents, customerConsents]);

  const initialValues = useMemo(
    () => ({
      ...customer,
      fullName: customer?.fullName || `${customer?.firstName || ''} ${customer?.lastName || ''}`.trim(),
      consents,
      consentsValues,
      confirmationPassword: '',
    }),
    [customer, consents, consentsValues],
  );

  function translateErrors(errors?: string[]): FormErrors {
    const formErrors: FormErrors = {};
    // Some errors are combined in a single CSV string instead of one string per error
    errors
      ?.flatMap((e) => e.split(','))
      .forEach((error) => {
        switch (error.trim()) {
          case 'Missing firstName':
            // formErrors.form = t('account.errors.first_name_required');
            formErrors.firstName = t('account.errors.first_name_required');
            break;
          case 'Missing lastName':
            // formErrors.form = t('account.errors.last_name_required');
            formErrors.lastName = t('account.errors.last_name_required');
            break;
          case 'Missing fullName':
            formErrors.fullName = t('account.errors.full_name_required');
            break;
          case 'fullName must not be greater than 50 characters.':
            formErrors.fullName = t('account.errors.full_name_too_long');
            break;
          case 'Invalid param email':
            formErrors.form = t('account.errors.validation_error');
            formErrors.email = t('account.errors.invalid_param_email');
            break;
          case 'Customer email already exists':
            formErrors.form = t('account.errors.validation_error');
            formErrors.email = t('account.errors.email_exists');
            break;
          case 'Please enter a valid e-mail address.':
            formErrors.form = t('account.errors.validation_error');
            formErrors.email = t('account.errors.please_enter_valid_email');
            break;
          case 'Invalid confirmationPassword': {
            formErrors.form = t('account.errors.validation_error');
            formErrors.confirmationPassword = t('account.errors.invalid_password');
            break;
          }
          case 'firstName can have max 30 characters.': {
            formErrors.form = t('account.errors.validation_error');
            formErrors.firstName = t('account.errors.first_name_too_long');
            break;
          }
          case 'lastName can have max 30 characters.': {
            formErrors.form = t('account.errors.validation_error');
            formErrors.lastName = t('account.errors.last_name_too_long');
            break;
          }
          // case 'firstName cannot contain special characters': {
          //   // formErrors.form = t('account.errors.validation_error');
          //   formErrors.firstName = t('account.errors.first_name_special_characters', 'First name cannot contain special characters');
          //   break;
          // }
          // case 'lastName cannot contain special characters': {
          //   // formErrors.form = t('account.errors.validation_error');
          //   formErrors.lastName = t('account.errors.last_name_special_characters', 'Last name cannot contain special characters');
          //   break;
          // }
          case 'fullName cannot contain special characters': {
            formErrors.form = t('account.errors.validation_error');
            formErrors.fullName = t('account.errors.full_name_special_characters', 'Full name cannot contain special characters');
            break;
          }
          case 'Email update not supported': {
            formErrors.form = t('account.errors.email_update_not_supported');
            break;
          }
          case 'Wrong email/password combination': {
            formErrors.form = t('account.errors.wrong_combination');
            break;
          }
          default: {
            formErrors.form = t('account.errors.unknown_error');
            logDev('Unknown error', error);
            break;
          }
        }
      });

    return formErrors;
  }

  function formSection(props: FormSectionProps<typeof initialValues, FormErrors>) {
    return {
      ...props,
      className: panelClassName,
      panelHeaderClassName,
      saveButton: t('account.save'),
      cancelButton: t('account.cancel'),
      content: (args: FormSectionContentArgs<typeof initialValues, string[]>) => {
        // This function just allows the sections below to use the FormError type instead of an array of errors
        const formErrors = translateErrors(args.errors);

        // Render the section content, but also add a warning text if there's a form level error
        return (
          <>
            {formErrors?.form ? <FormFeedback variant="error">{formErrors.form}</FormFeedback> : null}
            {props.content?.({ ...args, errors: formErrors })}
          </>
        );
      },
    };
  }

  const { profile } = useProfileStore();

  const deleteAccountClickHandler = async () => {
    if (isKatonCognitoEnabled) {
      navigate('/u/delete-account');
      return;
    }

    if (isCleeng) {
      navigate(
        modalURLFromLocation(location, 'delete-account-confirmation'),
      );
      return;
    }

    if (allSubscriptions == null) {
      navigate(modalURLFromLocation(location, shouldAddPassword ? 'warning-account-deletion' : 'delete-account'));
      return;
    }

    const hasActiveSubscription = allSubscriptions?.some((subscription) => subscription.status === 'active' || subscription.status === 'active_trial');

    // const oneDayInSeconds = 24 * 60 * 60;
    const currentTimestamp = Math.floor(Date.now() / 1000);

    const isSubscriptionCancelledAndExpired = allSubscriptions?.some((subscription) => {
      const newExpiredTimestamp = subscription?.expiresAt;
      // const newExpiredTimestamp = rawSubscriptionTimestamp - oneDayInSeconds;
      return subscription.status === 'cancelled' && newExpiredTimestamp >= currentTimestamp;
    });

    const validCancelledSubscriptions =
      allSubscriptions?.filter((subscription) => {
        const newExpiredTimestamp = subscription?.expiresAt;
        // const newExpiredTimestamp = rawSubscriptionTimestamp - oneDayInSeconds;
        return subscription.status === 'cancelled' && newExpiredTimestamp !== undefined && newExpiredTimestamp >= currentTimestamp;
      }) ?? [];

    const highestExpiresAt = validCancelledSubscriptions.length > 0 ? Math.max(...validCancelledSubscriptions.map((sub) => sub.expiresAt)) : undefined;

    let expiresAtObject: Date | undefined = undefined;

    if (highestExpiresAt !== undefined) {
      expiresAtObject = new Date((highestExpiresAt) * 1000);
    }

    const expiresAt = expiresAtObject ? expiresAtObject.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).replace(',', '') : '';

    if (hasActiveSubscription) {
      navigate(modalURLFromLocation(location, 'warning-active-subscribedItems'), {
        state: { expiresAt, shouldAddPassword },
      });
    } else if (isSubscriptionCancelledAndExpired) {
      navigate(modalURLFromLocation(location, 'warning-cancelled-subscribedItems'), {
        state: { expiresAt, shouldAddPassword },
      });
    } else {
      navigate(modalURLFromLocation(location, shouldAddPassword ? 'warning-account-deletion' : 'delete-account'));
    }
  };

  const EditChangePassword = () => {
    return (
      <span className={styles.textWithButtonContainer}>
        <div className={styles.information}>{t('account.change_password_info')}</div>
        <div>
          <Button
            label={shouldAddPassword ? t('account.add_password') : t('account.change_password')}
            type="button"
            size="large"
            variant="transparent"
            onClick={() => navigate(modalURLFromLocation(location, 'change-password'))}
          />
        </div>
      </span>
    );
  };
  return (
    <>
      <h1 className="hideUntilFocus">{t('nav.account')}</h1>
      <Form initialValues={initialValues}>
        {[
          formSection({
            label: t('account.about_you'),
            editButton: t('account.edit_information'),
            onSubmit: async (values) => {
              const errors: string[] = [];

              if (isSplitupFullname) {
                if (!values.firstName?.trim()) {
                  errors.push('Missing firstName');
                } else if (values.firstName.length > 30) {
                  errors.push('firstName can have max 30 characters.');
                }

                if (isLastnameRequired && !values.lastName?.trim()) {
                  errors.push('Missing lastName');
                } else if (values.lastName && values.lastName.length > 30) {
                  errors.push('lastName can have max 30 characters.');
                }
              } else {
                if (!values.fullName?.trim()) {
                  errors.push('Missing fullName');
                } else if (values.fullName.length > 50) {
                  errors.push('fullName must not be greater than 50 characters.');
                }
              }

              if (errors.length > 0) {
                throw new Error(errors.join(', '));
              }
              const consents = formatConsentsFromValues(publisherConsents, { ...values.metadata, ...values.consentsValues });

              let response = null;

              const finalFirstName = isKatonCognitoEnabled || isSplitupFullname ? values.firstName?.trim() || '' : values.fullName?.trim() || '';
              const finalLastName = isKatonCognitoEnabled || isSplitupFullname ? values.lastName?.trim() || '' : '';

              if (isKatonCognitoEnabled) {
                const input = {
                  uFirstName: finalFirstName,
                  uLastName: finalLastName,
                };
                await editProfile(input);

                useAccountStore.setState((s: any) => ({
                  ...s,
                  user: {
                    ...s.user,
                    firstName: finalFirstName,
                    lastName: finalLastName,
                    fullName: `${finalFirstName} ${finalLastName}`.trim(),
                  },
                }));
              } else if (isCustomApiEnabled) {
                const payload = {
                  uFullName: isSplitupFullname ? undefined : values.fullName?.trim(),
                  firstname: isSplitupFullname ? finalFirstName : undefined,
                  lastname: isSplitupFullname ? finalLastName : undefined,
                };
                response = await createUserProfile(JSON.stringify(payload));
                const userResponse = await getUserProfile();
                const result = await getUserProfileData({ idToken: '', refreshToken: '', emailId: '' }, userResponse);
                useAccountStore.setState((e: any) => ({
                  ...e,
                  user: result.user,
                }));
              } else {
                response = await accountController.updateUser({
                  firstName: finalFirstName,
                  lastName: finalLastName,
                  fullName: `${finalFirstName} ${finalLastName}`.trim() || '',
                  metadata: {
                    ...values.metadata,
                    ...formatConsentsToRegisterFields(consents),
                    consents: JSON.stringify(consents),
                  },
                });
              }

              announce(t('account.update_success', { section: t('account.about_you') }), 'success');

              return response;
            },

            content: (section) => (
              <>
                <div className={styles.fullNameContanier}>
                  {section.isEditing ? (
                    <>
                      {isSplitupFullname ? (
                        <>
                          <TextField
                            name="firstName"
                            label={t('account.firstname')}
                            value={section.values.firstName}
                            onChange={section.onChange}
                            error={!!section.errors?.firstName}
                            helperText={section.errors?.firstName}
                            disabled={section.isBusy}
                            editing={section.isEditing}
                            autoComplete="given-name"
                            lang={htmlLang}
                            required
                            maxLength={30}
                            placeholder={t('account:registration.first_name_placeholder')}
                          />
                          <TextField
                            name="lastName"
                            label={t('account.lastname')}
                            value={section.values.lastName}
                            onChange={section.onChange}
                            error={!!section.errors?.lastName}
                            helperText={section.errors?.lastName}
                            disabled={section.isBusy}
                            editing={section.isEditing}
                            autoComplete="family-name"
                            lang={htmlLang}
                            required={isLastnameRequired}
                            maxLength={30}
                            placeholder={t('account:registration.last_name_placeholder')}
                          />
                        </>
                      ) : (
                        <TextField
                          name="fullName"
                          label={t('account.fullName')}
                          value={section.values.fullName}
                          onChange={section.onChange}
                          error={!!section.errors?.fullName}
                          helperText={section.errors?.fullName}
                          disabled={section.isBusy}
                          editing={section.isEditing}
                          autoComplete="name"
                          lang={htmlLang}
                          required
                          maxLength={50}
                          placeholder={t('account:registration.full_name_placeholder')}
                        />
                      )}
                    </>
                  ) : (
                    <TextField
                      name="fullName"
                      label={t('account.fullName')}
                      value={isSplitupFullname ? `${section.values.firstName} ${section.values.lastName}` : section.values.fullName}
                      onChange={section.onChange}
                      error={!!section.errors?.fullName}
                      helperText={section.errors?.fullName}
                      disabled={section.isBusy}
                      editing={section.isEditing}
                      autoComplete="name"
                      lang={htmlLang}
                      // required
                      borderedReadOnlyMood={true}
                    />
                  )}
                </div>
              </>
            ),
          }),
          formSection({
            label: t('account.email'),
            onSubmit: async (values) => {
              if (!values.email || !values.confirmationPassword) {
                throw new Error('Wrong email/password combination');
              }
              // const response = await accountController.updateUser({
              //   email: values.email || '',
              //   confirmationPassword: values.confirmationPassword,
              // });
              const response = {};

              announce(t('account.update_success', { section: t('account.email') }), 'success');

              return response;
            },
            editButton: t('account.edit_account'),
            readOnly: true,
            content: (section) => (
              <>
                <div className={styles.emailContanier}>
                  <TextField
                    name="email"
                    // label={t('account.email')}
                    value={section.values.email || ''}
                    onChange={section.onChange}
                    error={!!section.errors?.email}
                    helperText={section.errors?.email}
                    disabled={section.isBusy}
                    editing={section.isEditing}
                    autoComplete="email"
                    borderedReadOnlyMood={true}
                  // required
                  />
                </div>
                {section.isEditing && (
                  <TextField
                    name="confirmationPassword"
                    label={t('account.confirm_password')}
                    value={section.values.confirmationPassword}
                    onChange={section.onChange}
                    error={!!section.errors?.confirmationPassword}
                    helperText={section.errors?.confirmationPassword}
                    type={viewPassword ? 'text' : 'password'}
                    disabled={section.isBusy}
                    autoComplete="current-password"
                    rightControl={
                      <IconButton aria-label={t('account.view_password')} onClick={() => toggleViewPassword()} aria-pressed={viewPassword}>
                        <Icon icon={viewPassword ? Visibility : VisibilityOff} />
                      </IconButton>
                    }
                    required
                  />
                )}
              </>
            ),
          }),
          !isSocialLogin &&
          (
            (canChangePassword &&
              !cognitoUser?.signInUserSession?.idToken?.payload?.identities?.length &&
              cognitoUser?.signInUserSession?.idToken?.payload?.event_id) ||
            (canChangePassword &&
              cognitoUser?.signInUserSession?.idToken?.payload?.identities?.length &&
              cognitoUser?.signInUserSession?.idToken?.payload?.event_id) ||
            (isInplayer) ||
            (canChangePassword && isKatonCognitoEnabled)
          ) &&
          formSection({
            label: t('account.security'),
            readOnly: true,
            content: () => (
              <div className={styles.textWithButtonContainer}>
                {showPasswordContent ? (
                  <>
                    <EditAccountPassword setShowPasswordContent={setShowPasswordContent} />
                  </>
                ) : (
                  <>
                    <div className={styles.information}>{t('account.change_password_info')}</div>
                    <div>
                      <Button
                        label={shouldAddPassword ? t('account.add_password') : t('account.change_password')}
                        type="button"
                        size="large"
                        variant="transparent"
                        onClick={() => setShowPasswordContent(true)}
                      />
                    </div>
                  </>
                )}
              </div>
            ),
          }),

          canChangePassword &&
          !isSocialLogin &&
          isCleeng &&
          formSection({
            label: t('account.security'),
            content: () => <EditChangePassword />,
          }),

          canDeleteAccount &&
          formSection({
            label: t('account.delete_account.title'),
            readOnly: true,
            content: () => (
              <div className={styles.textWithButtonContainer}>
                <div className={styles.information}>{t('account.delete_account.body')}</div>
                <div>
                  <Button label={t('account.delete_account.title')} type="button" variant="dangerText" size="large" onClick={deleteAccountClickHandler} />
                </div>
              </div>
            ),
          }),
        ].filter(isTruthy)}
      </Form>
      {canShowExportData && (
        <Alert open={isAlertVisible} message={exportDataMessage} onClose={() => setIsAlertVisible(false)} isSuccess={exportData.isSuccess} />
      )}
    </>
  );
};

export default Account;
